import { useScroll, useTransform } from 'framer-motion';
import type { RefObject } from 'react';
import { useEffect, useState } from 'react';

import {
  desktopNavbarHeight,
  navbarGlassBlur,
  navbarGlassOpacity,
  useNavbarGlass,
} from './constants';

export interface NavbarScrollOptions<
  TElement extends HTMLElement = HTMLElement,
> {
  /**
   * Multiplier to apply to final scroll position to adjust the
   * interpolation relative to the navbar interpolation
   */
  multiplier?: number;
  /**
   * Element to derive the scroll offset from (if `offset` is provided it will override this)
   */
  offsetElementRef?: RefObject<TElement>;
}

/**
 * Interpolate a value based on scroll position until
 * the navbar is fully in its "floating" styled state
 *
 * @param start value to interpolate from
 * @param end value to interpolate to
 * @param multiplier multiplier to apply to final scroll position to adjust the
 * interpolation relative to the navbar interpolation
 *
 * @returns motion value for use with Framer Motion
 */

const useNavbarScrollValue = <TValue extends {}, TElement extends HTMLElement>(
  start: TValue,
  end: TValue,
  options: NavbarScrollOptions<TElement> = {},
) => {
  const [offset, setOffset] = useState(0);

  const { multiplier = 1, offsetElementRef } = options;
  const { scrollY } = useScroll();

  useEffect(() => {
    if (!offsetElementRef?.current) {
      setOffset(0);
      return;
    }

    const { top } = offsetElementRef.current.getBoundingClientRect();

    if (top > 0) setOffset(top);
  }, [offsetElementRef]);

  return useTransform(
    scrollY,
    [offset, desktopNavbarHeight * multiplier + offset],
    [start, end],
  );
};

/**
 * Apply a background color and blur to a navbar based on scroll position
 *
 * @returns `style` object to be passed or spread into the `style` prop of a
 * motion component
 */
const useNavbarScrollBackground = <TElement extends HTMLElement>(
  opacity: number = navbarGlassOpacity,
  options?: NavbarScrollOptions<TElement>,
) => {
  const opacityMotion = useNavbarScrollValue(0, opacity, options);
  const blurMotion = useNavbarScrollValue(0, navbarGlassBlur, options);

  return useNavbarGlass(opacityMotion, blurMotion);
};

export { useNavbarScrollValue, useNavbarScrollBackground };
